import { apiClient } from "./index";

// Get Supplier returns count statistics
export const getVendorReturnsCount = async () => {
  try {
    const response = await apiClient.get("/return-management/vendors/count/");

    // Handle the response structure - API returns flat object with count values
    const data = response.data || {};

    // Normalize the response structure to match component expectations
    // API response: { total_count, active_count, completed_count, total_value }
    const normalizedData = {
      active_records: data.active_count || 0,
      delivered_records: data.completed_count || 0,
      total_return_value: data.total_value || 0,
    };

    return { response: normalizedData, error: null };
  } catch (error) {
    console.error("Error fetching Supplier returns count:", error);
    return {
      response: null,
      error: {
        message:
          error.response?.data?.message ||
          error.message ||
          "Failed to fetch Supplier returns count",
        data: error.response?.data,
        status: error.response?.status,
      },
    };
  }
};

// Get Supplier returns list with filters and pagination
export const getVendorReturnsList = async (
  search = "",
  page = 1,
  pageSize = 25,
  status = "",
  dateFilter = "",
  type = ""
) => {
  try {
    const params = new URLSearchParams();
    if (search) params.append("search", search);
    if (page) params.append("page", page.toString());
    if (pageSize) params.append("page_size", pageSize.toString());
    if (status && status !== "all") params.append("status", status);
    if (dateFilter && dateFilter !== "all")
      params.append("date_filter", dateFilter);
    if (type) params.append("type", type);

    const response = await apiClient.get(
      `/return-management/vendors/list/?${params.toString()}`
    );
    return { response: response.data, error: null };
  } catch (error) {
    console.error("Error fetching Supplier returns list:", error);
    return {
      response: null,
      error: {
        message:
          error.response?.data?.message ||
          error.message ||
          "Failed to fetch Supplier returns list",
        data: error.response?.data,
        status: error.response?.status,
      },
    };
  }
};

// Get Supplier return by ID
export const getVendorReturnById = async (returnId) => {
  try {
    const response = await apiClient.get(
      `/return-management/vendors/${returnId}/`
    );
    return { response: response.data, error: null };
  } catch (error) {
    console.error("Error fetching Supplier return by ID:", error);
    return {
      response: null,
      error: {
        message:
          error.response?.data?.message ||
          error.message ||
          "Failed to fetch Supplier return",
        data: error.response?.data,
        status: error.response?.status,
      },
    };
  }
};

// Get Supplier return detail with filters and pagination
export const getVendorReturnDetail = async (
  returnId,
  search = "",
  page = 1,
  pageSize = 25,
  status = "",
  dateFilter = "",
  type = "",
  deliveredReportFilter = "",
  vendorReportFilter = ""
) => {
  try {
    const params = new URLSearchParams();
    if (search) params.append("search", search);
    if (page) params.append("page", page.toString());
    if (pageSize) params.append("page_size", pageSize.toString());
    if (status && status !== "all") params.append("status", status);
    if (dateFilter && dateFilter !== "all")
      params.append("date_filter", dateFilter);
    if (type) params.append("type", type);
    if (deliveredReportFilter && deliveredReportFilter !== "all")
      params.append("delivered_report", deliveredReportFilter);
    if (vendorReportFilter && vendorReportFilter !== "all")
      params.append("vendor_report", vendorReportFilter);

    const response = await apiClient.get(
      `/return-management/vendors/${returnId}/detail/?${params.toString()}`
    );
    return { response: response.data, error: null };
  } catch (error) {
    console.error("Error fetching Supplier return detail:", error);
    return {
      response: null,
      error: {
        message:
          error.response?.data?.message ||
          error.message ||
          "Failed to fetch Supplier return detail",
        data: error.response?.data,
        status: error.response?.status,
      },
    };
  }
};

// Update Supplier return
export const updateVendorReturn = async (returnId, returnData) => {
  try {
    const response = await apiClient.patch(
      `/return-management/vendors/${returnId}/`,
      returnData
    );
    return { response: response.data, error: null };
  } catch (error) {
    console.error("Error updating Supplier return:", error);
    const errorResponse = error.response?.data || {};

    // Extract error message from backend response
    let errorMessage =
      errorResponse.message ||
      errorResponse.error ||
      errorResponse.detail ||
      error.message ||
      "Failed to update Supplier return";

    // Handle validation errors
    if (errorResponse.errors || errorResponse.non_field_errors) {
      const validationErrors =
        errorResponse.errors || errorResponse.non_field_errors;
      if (Array.isArray(validationErrors)) {
        errorMessage = validationErrors.join(", ");
      } else if (typeof validationErrors === "object") {
        errorMessage = Object.entries(validationErrors)
          .map(
            ([key, value]) =>
              `${key}: ${Array.isArray(value) ? value.join(", ") : value}`
          )
          .join(", ");
      } else {
        errorMessage = validationErrors;
      }
    }

    return {
      response: null,
      error: {
        message: errorMessage,
        data: errorResponse,
        status: error.response?.status,
      },
    };
  }
};

// Update Supplier return status
export const updateVendorReturnStatus = async (returnId, status) => {
  try {
    const response = await apiClient.patch(
      `/return-management/vendors/${returnId}/status/`,
      { status }
    );
    return { response: response.data, error: null };
  } catch (error) {
    console.error("Error updating Supplier return status:", error);
    const errorResponse = error.response?.data || {};

    // Extract error message from backend response
    let errorMessage =
      errorResponse.message ||
      errorResponse.error ||
      errorResponse.detail ||
      error.message ||
      "Failed to update Supplier return status";

    // Handle validation errors
    if (errorResponse.errors || errorResponse.non_field_errors) {
      const validationErrors =
        errorResponse.errors || errorResponse.non_field_errors;
      if (Array.isArray(validationErrors)) {
        errorMessage = validationErrors.join(", ");
      } else if (typeof validationErrors === "object") {
        errorMessage = Object.entries(validationErrors)
          .map(
            ([key, value]) =>
              `${key}: ${Array.isArray(value) ? value.join(", ") : value}`
          )
          .join(", ");
      } else {
        errorMessage = validationErrors;
      }
    }

    return {
      response: null,
      error: {
        message: errorMessage,
        data: errorResponse,
        status: error.response?.status,
      },
    };
  }
};

// Delete Supplier return (entire delivery record)
export const deleteVendorReturn = async (returnId) => {
  try {
    const response = await apiClient.delete(
      `/return-management/vendors/${returnId}/`
    );
    return { response: response.data, error: null };
  } catch (error) {
    console.error("Error deleting Supplier return:", error);
    const errorResponse = error.response?.data || {};

    // Extract error message from backend response
    let errorMessage =
      errorResponse.message ||
      errorResponse.error ||
      errorResponse.detail ||
      error.message ||
      "Failed to delete Supplier return";

    // Handle validation errors
    if (errorResponse.errors || errorResponse.non_field_errors) {
      const validationErrors =
        errorResponse.errors || errorResponse.non_field_errors;
      if (Array.isArray(validationErrors)) {
        errorMessage = validationErrors.join(", ");
      } else if (typeof validationErrors === "object") {
        errorMessage = Object.entries(validationErrors)
          .map(
            ([key, value]) =>
              `${key}: ${Array.isArray(value) ? value.join(", ") : value}`
          )
          .join(", ");
      } else {
        errorMessage = validationErrors;
      }
    }

    return {
      response: null,
      error: {
        message: errorMessage,
        data: errorResponse,
        status: error.response?.status,
      },
    };
  }
};

// Delete Supplier return transaction (individual item within a delivery)
export const deleteVendorReturnTransaction = async (deliveryId, transactionId) => {
  try {
    const response = await apiClient.delete(
      `/return-management/vendors/${deliveryId}/transactions/${transactionId}/`
    );
    return { response: response.data, error: null };
  } catch (error) {
    console.error("Error deleting Supplier return transaction:", error);
    const errorResponse = error.response?.data || {};

    // Extract error message from backend response
    let errorMessage =
      errorResponse.message ||
      errorResponse.error ||
      errorResponse.detail ||
      error.message ||
      "Failed to delete transaction";

    // Handle validation errors
    if (errorResponse.errors || errorResponse.non_field_errors) {
      const validationErrors =
        errorResponse.errors || errorResponse.non_field_errors;
      if (Array.isArray(validationErrors)) {
        errorMessage = validationErrors.join(", ");
      } else if (typeof validationErrors === "object") {
        errorMessage = Object.entries(validationErrors)
          .map(
            ([key, value]) =>
              `${key}: ${Array.isArray(value) ? value.join(", ") : value}`
          )
          .join(", ");
      } else {
        errorMessage = validationErrors;
      }
    }

    return {
      response: null,
      error: {
        message: errorMessage,
        data: errorResponse,
        status: error.response?.status,
      },
    };
  }
};

// Update device by device ID (using return_device_id)
export const updateDeviceById = async (deviceId, deviceData) => {
  try {
    const response = await apiClient.patch(
      `/return-management/devices/${deviceId}/`,
      deviceData
    );
    return { response: response.data, error: null };
  } catch (error) {
    console.error("Error updating device:", error);
    const errorResponse = error.response?.data || {};

    // Extract error message from backend response
    let errorMessage =
      errorResponse.message ||
      errorResponse.error ||
      errorResponse.detail ||
      error.message ||
      "Failed to update device";

    // Handle validation errors
    if (errorResponse.errors || errorResponse.non_field_errors) {
      const validationErrors =
        errorResponse.errors || errorResponse.non_field_errors;
      if (Array.isArray(validationErrors)) {
        errorMessage = validationErrors.join(", ");
      } else if (typeof validationErrors === "object") {
        errorMessage = Object.entries(validationErrors)
          .map(
            ([key, value]) =>
              `${key}: ${Array.isArray(value) ? value.join(", ") : value}`
          )
          .join(", ");
      } else {
        errorMessage = validationErrors;
      }
    }

    return {
      response: null,
      error: {
        message: errorMessage,
        data: errorResponse,
        status: error.response?.status,
      },
    };
  }
};

// Get device details by device ID
export const getDeviceById = async (deviceId) => {
  try {
    const response = await apiClient.get(
      `/return-management/devices/${deviceId}/`
    );
    return { response: response.data, error: null };
  } catch (error) {
    console.error("Error fetching device details:", error);
    return {
      response: null,
      error: {
        message:
          error.response?.data?.message ||
          error.message ||
          "Failed to fetch device details",
        data: error.response?.data,
        status: error.response?.status,
      },
    };
  }
};
